import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { type ErroApi } from '@/nucleo/auth/cliente-api';
import { MOCK_DRES } from '@/modulos/financeiro/dados-simulados';
import { naoInsistirEmRecusa } from '@/nucleo/naoInsistirEmRecusa';

import type {
  BarraDeLotesPorDre,
  Competencia,
  DreEmDefasagem,
  LoteProntoParaAprovacao,
  PainelSme,
  VolumeAprovadoNoMes,
} from './contrato';

/**
 * A camada de dados do Painel SME.
 *
 * Mesmo desenho das outras telas: nenhum componente chama `chamarApi` direto e a
 * chave de cache mora junto de quem a usa (CLAUDE.md, "Regras de API").
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend. Não existe
 * módulo de painel em `apps/api/src/modulos/`, e as tabelas que o alimentariam
 * (`apontamento_lotes`, `solicitacoes_sme`, `revisoes_medicao`, `medicoes`) já
 * estão migradas mas não têm rota que as agregue. Decisão do dono de 2026-09-22
 * (CLAUDE.md, "Proibido", exceção estendida ao portal SME) para demonstração à
 * SMT/DTP — mesmas três condições de sempre: só leitura, dado claramente
 * fictício, removido antes de `develop` → `hml` (`git grep -n "TEMPORÁRIO"
 * apps/web/src`, checklist em `docs/ALVOS.md`).
 *
 * NENHUMA DATA LITERAL. Toda data do mock é contada a partir do relógio de quem
 * executa: data fixa envelhece sozinha, um dia esvazia a tela e derruba o teste
 * numa PR que não tem nada a ver com ela.
 *
 * AS DREs SÃO AS DO RESTO DO SISTEMA (`financeiro/dados-simulados.ts`). Uma
 * segunda lista de diretorias faria a mesma demonstração mostrar um parque
 * diferente a cada tela.
 */

export const CHAVES_DO_PAINEL_SME = {
  painel: (competencia: Competencia) => ['sme', 'painel', competencia] as const,
};

/** Competência de referência: o mês anterior ao de quem executa. */
export function competenciaDeReferencia(): Competencia {
  const hoje = new Date();
  const anterior = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return `${anterior.getFullYear()}-${String(anterior.getMonth() + 1).padStart(2, '0')}`;
}

/** Desloca uma competência `yyyy-mm` em `meses` (negativo volta no tempo). */
function deslocarCompetencia(competencia: Competencia, meses: number): Competencia {
  const partes = competencia.split('-').map(Number);
  const ano = partes[0] ?? new Date().getFullYear();
  const mes = partes[1] ?? 1;
  const data = new Date(ano, mes - 1 + meses, 1);
  return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
}

/**
 * A diretoria pelo código, tirada da lista compartilhada.
 *
 * `DreOpcao.codigo` e `DreOpcao.nome` são anuláveis no contrato do financeiro
 * (vêm nulos quando a API ainda não classificou a diretoria); aqui o código é a
 * chave da busca, então quem não tem código simplesmente não é encontrado — e
 * erro de mock é erro de programação, não situação de usuário.
 */
function dre(codigo: string): { id: string; codigo: string; nome: string } {
  const encontrada = MOCK_DRES.find((item) => item.codigo === codigo);
  if (!encontrada?.codigo) {
    throw new Error(`DRE ${codigo} não existe na lista compartilhada de diretorias.`);
  }
  return {
    id: encontrada.id,
    codigo: encontrada.codigo,
    nome: encontrada.nome ?? encontrada.codigo,
  };
}

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// As cinco diretorias paradas da demonstração, com a idade do lote mais antigo.
const DEFASAGEM_MOCK: Array<{
  codigo: string;
  lotesParados: number;
  diasParadoMaximo: number;
  cobrancasDisparadas: number;
}> = [
  { codigo: 'BT', lotesParados: 5, diasParadoMaximo: 132, cobrancasDisparadas: 0 },
  { codigo: 'PJ', lotesParados: 2, diasParadoMaximo: 85, cobrancasDisparadas: 0 },
  { codigo: 'SA', lotesParados: 7, diasParadoMaximo: 85, cobrancasDisparadas: 0 },
  { codigo: 'CL', lotesParados: 5, diasParadoMaximo: 80, cobrancasDisparadas: 0 },
  { codigo: 'G', lotesParados: 5, diasParadoMaximo: 80, cobrancasDisparadas: 0 },
];

/** Acima disto a SME trata a defasagem como crítica (mesmo corte de `DRE_DEFASADA`). */
const DIAS_PARA_DEFASAGEM_CRITICA = 7;

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Nomes de empresa claramente fictícios, os mesmos já usados no módulo
// financeiro — nenhum credenciado real de São Paulo.
const LOTES_PRONTOS_MOCK: Array<{
  loteId: string;
  credenciadoNome: string;
  dreCodigo: string;
  mesesAtras: number;
  diasApontados: number;
  diasAguardando: number;
}> = [
  {
    loteId: 'lote-sme-01',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    dreCodigo: 'BT',
    mesesAtras: 5,
    diasApontados: 22,
    diasAguardando: 132,
  },
  {
    loteId: 'lote-sme-02',
    credenciadoNome: 'Viação Escolar Zona Leste',
    dreCodigo: 'SA',
    mesesAtras: 1,
    diasApontados: 23,
    diasAguardando: 85,
  },
  {
    loteId: 'lote-sme-03',
    credenciadoNome: 'Escolar Aurora ME',
    dreCodigo: 'SA',
    mesesAtras: 1,
    diasApontados: 23,
    diasAguardando: 85,
  },
  {
    loteId: 'lote-sme-04',
    credenciadoNome: 'Cooperativa Rota Segura',
    dreCodigo: 'PJ',
    mesesAtras: 1,
    diasApontados: 46,
    diasAguardando: 85,
  },
  {
    loteId: 'lote-sme-05',
    credenciadoNome: 'Transporte Estudantil Bem-Me-Quer',
    dreCodigo: 'CL',
    mesesAtras: 4,
    diasApontados: 12,
    diasAguardando: 80,
  },
];

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
const LOTES_POR_DRE_MOCK: Array<{ codigo: string; quantidade: number }> = [
  { codigo: 'BT', quantidade: 5 },
  { codigo: 'SA', quantidade: 4 },
  { codigo: 'CL', quantidade: 4 },
  { codigo: 'PJ', quantidade: 3 },
  { codigo: 'G', quantidade: 3 },
  { codigo: 'IQ', quantidade: 2 },
  { codigo: 'MP', quantidade: 2 },
];

// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
// Volume aprovado dos seis meses que terminam na competência, em reais.
const VOLUME_APROVADO_MOCK: number[] = [84_500, 612_300, 941_800, 706_400, 58_900, 47_200];

/**
 * O painel da competência de referência.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
 */
function painelDaReferencia(competencia: Competencia): PainelSme {
  const dresEmDefasagem: DreEmDefasagem[] = DEFASAGEM_MOCK.map((linha) => {
    const diretoria = dre(linha.codigo);
    return {
      dreId: diretoria.id,
      dreCodigo: diretoria.codigo,
      dreNome: diretoria.nome,
      lotesParados: linha.lotesParados,
      diasParadoMaximo: linha.diasParadoMaximo,
      cobrancasDisparadas: linha.cobrancasDisparadas,
      critica: linha.diasParadoMaximo > DIAS_PARA_DEFASAGEM_CRITICA,
    };
  });

  const lotesProntosParaAprovacao: LoteProntoParaAprovacao[] = LOTES_PRONTOS_MOCK.map((linha) => ({
    loteId: linha.loteId,
    credenciadoNome: linha.credenciadoNome,
    dreCodigo: dre(linha.dreCodigo).codigo,
    competencia: deslocarCompetencia(competencia, -linha.mesesAtras),
    diasApontados: linha.diasApontados,
    diasAguardando: linha.diasAguardando,
  }));

  const lotesPorDre: BarraDeLotesPorDre[] = LOTES_POR_DRE_MOCK.map((linha) => ({
    dreCodigo: dre(linha.codigo).codigo,
    quantidade: linha.quantidade,
  }));

  const volumeAprovado: VolumeAprovadoNoMes[] = VOLUME_APROVADO_MOCK.map((valor, indice) => ({
    competencia: deslocarCompetencia(competencia, indice - (VOLUME_APROVADO_MOCK.length - 1)),
    valorAprovado: valor,
  }));

  return {
    competencia,
    calculadoEm: new Date().toISOString(),
    indicadores: {
      dresEmAtrasoDeEnvio: 3,
      dresComLoteVencido: 5,
      lotesAcimaDoSla: 12,
      lotesAguardandoAprovacao: 23,
      lotesDevolvidos: 6,
      revisoesAbertas: 4,
      aguardandoDocumentos: 9,
      medicoesDoMes: 148,
      demandasEmAberto: 91,
      cobrancasNoMes: 0,
      dresAlcancadasPorCobranca: 0,
    },
    variacoes: {
      lotesAguardandoAprovacao: 13,
      lotesDevolvidos: 8,
      revisoesAbertas: 0,
      aguardandoDocumentos: 4,
      medicoesDoMes: -21,
    },
    dresEmDefasagem,
    lotesProntosParaAprovacao,
    lotesPorStatus: [
      { situacao: 'Rascunho (DRE)', quantidade: 16 },
      { situacao: 'Enviado', quantidade: 23 },
      { situacao: 'Devolvido', quantidade: 6 },
      { situacao: 'Aprovado', quantidade: 104 },
    ],
    lotesPorDre,
    volumeAprovado,
    mensagensNaoLidas: 1,
    documentosPendentesDeValidacao: 9,
  };
}

/**
 * O painel de uma competência sem movimento.
 *
 * O mock cobre uma competência só — a de referência. Escolher outro mês devolve
 * isto, e a tela mostra o estado vazio de verdade em vez de repetir os mesmos
 * números em todo mês do calendário, que seria afirmar o que ninguém apurou.
 */
function painelVazio(competencia: Competencia): PainelSme {
  return {
    competencia,
    calculadoEm: new Date().toISOString(),
    indicadores: {
      dresEmAtrasoDeEnvio: 0,
      dresComLoteVencido: 0,
      lotesAcimaDoSla: 0,
      lotesAguardandoAprovacao: 0,
      lotesDevolvidos: 0,
      revisoesAbertas: 0,
      aguardandoDocumentos: 0,
      medicoesDoMes: 0,
      demandasEmAberto: 0,
      cobrancasNoMes: 0,
      dresAlcancadasPorCobranca: 0,
    },
    variacoes: {},
    dresEmDefasagem: [],
    lotesProntosParaAprovacao: [],
    lotesPorStatus: [],
    lotesPorDre: [],
    volumeAprovado: [],
    mensagensNaoLidas: 0,
    documentosPendentesDeValidacao: 0,
  };
}

/**
 * O painel da competência escolhida.
 *
 * TEMPORÁRIO — mock local, remover no desenvolvimento do backend: devolve o
 * agregado fictício em vez de buscar em `/sme/painel`, que ainda não existe.
 */
export function usePainelSme(competencia: Competencia): UseQueryResult<PainelSme, ErroApi> {
  return useQuery<PainelSme, ErroApi>({
    queryKey: CHAVES_DO_PAINEL_SME.painel(competencia),
    queryFn: async () =>
      competencia === competenciaDeReferencia()
        ? painelDaReferencia(competencia)
        : painelVazio(competencia),
    retry: naoInsistirEmRecusa,
  });
}
